import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SearchableSelect.css";

const sortByLabel = (options) =>
  [...options].sort((a, b) =>
    String(a.label || "").localeCompare(String(b.label || ""), undefined, {
      sensitivity: "base",
    }),
  );

const SearchableSelect = ({
  label,
  value,
  onChange,
  options = [],
  allLabel = "All",
  allValue = "0",
  placeholder = "Search...",
  multiple = false,
  maxSelections,
}) => {
  const hasSelectionLimit =
    Number.isFinite(maxSelections) && maxSelections > 0;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  const sortedOptions = useMemo(() => sortByLabel(options), [options]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedOptions;
    return sortedOptions.filter((opt) =>
      String(opt.label || "")
        .toLowerCase()
        .includes(q),
    );
  }, [sortedOptions, query]);

  const selectedValues = useMemo(() => {
    if (!multiple) return null;
    const list = Array.isArray(value) ? value : value ? [value] : [allValue];
    return list.map(String);
  }, [multiple, value, allValue]);

  const isOptionSelected = (optionValue) => {
    if (multiple) {
      if (String(optionValue) === String(allValue)) {
        return (
          selectedValues.includes(String(allValue)) ||
          selectedValues.length === 0
        );
      }
      return selectedValues.includes(String(optionValue));
    }
    return String(value) === String(optionValue);
  };

  const selectedLabel = useMemo(() => {
    if (multiple) {
      if (!selectedValues.length || selectedValues.includes(String(allValue))) {
        return allLabel;
      }

      const labels = selectedValues
        .map((id) => {
          const found = sortedOptions.find(
            (opt) => String(opt.value) === String(id),
          );
          return found?.label;
        })
        .filter(Boolean);

      if (!labels.length) return allLabel;
      if (labels.length <= 2) return labels.join(", ");
      return `${labels.length} channels selected`;
    }

    if (String(value) === String(allValue)) return allLabel;
    const found = sortedOptions.find(
      (opt) => String(opt.value) === String(value),
    );
    return found?.label || allLabel;
  }, [multiple, value, selectedValues, sortedOptions, allLabel, allValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (nextValue) => {
    if (!multiple) {
      onChange({ target: { value: nextValue } });
      setOpen(false);
      setQuery("");
      return;
    }

    const next = String(nextValue);

    if (next === String(allValue)) {
      onChange({ target: { value: [allValue] } });
      setOpen(false);
      setQuery("");
      return;
    }

    let current = (Array.isArray(value) ? value : [])
      .map(String)
      .filter((id) => id !== String(allValue));

    const existingIndex = current.indexOf(next);
    if (existingIndex >= 0) {
      current = current.filter((id) => id !== next);
    } else if (hasSelectionLimit && current.length >= maxSelections) {
      return;
    } else {
      current = [...current, next];
    }

    onChange({
      target: {
        value: current.length ? current : [allValue],
      },
    });
  };

  return (
    <div className="monitoring-field searchable-select" ref={rootRef}>
      {label ? <label className="monitoring-label">{label}</label> : null}
      <div className={`searchable-select__control${open ? " is-open" : ""}`}>
        <button
          type="button"
          className="searchable-select__trigger monitoring-input"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="searchable-select__value">{selectedLabel}</span>
          <span className="searchable-select__chevron" aria-hidden="true" />
        </button>
        {open ? (
          <div className="searchable-select__dropdown">
            <input
              type="search"
              className="searchable-select__search"
              placeholder={placeholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
            />
            <ul className="searchable-select__list" role="listbox">
              <li>
                <button
                  type="button"
                  className={`searchable-select__option${
                    isOptionSelected(allValue) ? " is-selected" : ""
                  }`}
                  onClick={() => handleSelect(allValue)}
                >
                  {multiple ? (
                    <span
                      className={`searchable-select__check${
                        isOptionSelected(allValue) ? " is-checked" : ""
                      }`}
                      aria-hidden="true"
                    />
                  ) : null}
                  {allLabel}
                </button>
              </li>
              {filteredOptions.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    className={`searchable-select__option${
                      isOptionSelected(opt.value) ? " is-selected" : ""
                    }`}
                    onClick={() => handleSelect(opt.value)}
                  >
                    {multiple ? (
                      <span
                        className={`searchable-select__check${
                          isOptionSelected(opt.value) ? " is-checked" : ""
                        }`}
                        aria-hidden="true"
                      />
                    ) : null}
                    {opt.label}
                  </button>
                </li>
              ))}
              {filteredOptions.length === 0 ? (
                <li className="searchable-select__empty">No results</li>
              ) : null}
            </ul>
            {multiple && hasSelectionLimit ? (
              <div className="searchable-select__hint">
                Select up to {maxSelections} channels to compare
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchableSelect;
